import { observer } from "mobx-react-lite";
import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { useNavigate } from "react-router-dom";
import { PAGE_SHELL_BG } from "../utils/pageShellBg";
import { PAGE_TITLE_BRAND } from "../utils/pageTitle";
import { h5NavigateTo } from "../utils/miniProgramEnv";
import { usePageShellBg } from "../utils/usePageShellBg";
import { useSafeAreaTopPad } from "../utils/useSafeAreaTopPad";
import { platformHomeLayoutCssVars, platformHomeStarwallMaskStyle } from "./Config";
import { PlatformHomeApi } from "./PlatformHomeApi";
import { PlatformHomeModel } from "./PlatformHomeModel";
import type { RandomSpaceAccessPublicConfig } from "./PlatformHomeType";
import { resolveRandomEnterButtonText } from "./PlatformHomeType";
import "./PlatformHomeStyle.css";

const P = process.env.PUBLIC_URL || "";
const SPACE_ID_LEN = 8;

const PlatformHomePage = observer(() => {
  const model = useMemo(() => new PlatformHomeModel(), []);
  const navigate = useNavigate();
  const enterGenRef = useRef(0);
  const idInputRef = useRef<HTMLInputElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const safeTopPad = useSafeAreaTopPad(rootRef);
  const [randomAccess, setRandomAccess] = useState<RandomSpaceAccessPublicConfig>({
    enabled: false,
    candidates: [],
    bigButton: false,
    buttonText: "",
  });

  usePageShellBg({
    bg: PAGE_SHELL_BG.platformHome,
    front: "#ffffff",
    title: PAGE_TITLE_BRAND,
  });

  const goSpaceHome = (id: string) => {
    h5NavigateTo(navigate, `/space/${id}/home`);
  };

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const config = await PlatformHomeApi.getRandomSpaceAccessConfig();
      if (!cancelled) {
        setRandomAccess(config);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const v = model.spaceIdInput;
    if (!/^\d{8}$/.test(v)) {
      return;
    }
    const gen = ++enterGenRef.current;
    void (async () => {
      const exists = await PlatformHomeApi.checkSpaceExists(v);
      if (gen !== enterGenRef.current) {
        return;
      }
      if (!exists) {
        model.setEntranceError("您输入的空间ID不存在");
        return;
      }
      goSpaceHome(v);
    })();
  }, [model, model.spaceIdInput, navigate]);

  const handleRandomEnter = () => {
    const list = randomAccess.candidates;
    if (!list.length) {
      return;
    }
    const idx = Math.floor(Math.random() * list.length);
    model.setEntranceError(null);
    goSpaceHome(list[idx]!);
  };

  const showRandomEnter =
    randomAccess.enabled && randomAccess.candidates.length > 0;

  return (
    <div
      ref={rootRef}
      className="platform-home-root"
      style={
        {
          ...platformHomeLayoutCssVars(),
          "--platform-home-safe-top-pad": `${safeTopPad}px`,
        } as CSSProperties
      }
    >
      <p className="platform-home-tagline">财经资讯和知识分享平台</p>

      <div className="platform-home-main">
        <div className="platform-home-brand-row">
          <img
            className="platform-home-logo"
            src={`${P}/logo.png`}
            alt=""
            draggable={false}
          />
          <img
            className="platform-home-wordmark"
            src={`${P}/chinese-webname-white.png`}
            alt="龙粉社"
            draggable={false}
          />
        </div>

        <div className="platform-home-form">
          <div className="platform-home-input-panel">
            <p className="platform-home-input-label">输入8位龙粉号空间ID</p>

            <div
              className="platform-home-id-cells"
              onClick={() => idInputRef.current?.focus()}
            >
              {Array.from({ length: SPACE_ID_LEN }, (_, i) => {
                const char = model.spaceIdInput[i] ?? "";
                const isActive =
                  !model.entranceError && i === model.spaceIdInput.length;
                return (
                  <span
                    key={i}
                    className={[
                      "platform-home-id-cell",
                      model.entranceError ? "platform-home-id-cell--error" : "",
                      isActive ? "platform-home-id-cell--active" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  >
                    {char}
                  </span>
                );
              })}
              <input
                ref={idInputRef}
                className="platform-home-id-input-hidden"
                type="text"
                inputMode="numeric"
                autoComplete="off"
                autoFocus
                maxLength={SPACE_ID_LEN}
                value={model.spaceIdInput}
                onChange={(e) => model.setSpaceIdInput(e.target.value)}
                aria-label="输入8位龙粉号空间ID"
              />
            </div>

            <button
              type="button"
              className={[
                "platform-home-random-enter",
                randomAccess.bigButton ? "platform-home-random-enter--big" : "",
              ]
                .filter(Boolean)
                .join(" ")}
              onClick={handleRandomEnter}
              disabled={!showRandomEnter}
            >
              {resolveRandomEnterButtonText(randomAccess.buttonText)}
            </button>
            {model.entranceError ? (
              <p className="platform-home-entrance-error" role="alert">
                {model.entranceError}
              </p>
            ) : null}
          </div>
        </div>

        <div className="platform-home-starwall-wrap">
          <div
            className="platform-home-starwall-frame"
            style={platformHomeStarwallMaskStyle()}
          >
            <img
              className="platform-home-starwall"
              src={`${P}/starwall.png`}
              alt=""
              draggable={false}
            />
          </div>
        </div>

        <div className="platform-home-longfans-wrap">
          <img
            className="platform-home-longfans-img"
            src={`${P}/longfans.png`}
            alt="LONGFANS"
            draggable={false}
          />
        </div>
      </div>

      <p className="platform-home-slogan">与远见者，长期同行</p>
    </div>
  );
});

export default PlatformHomePage;
